import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import dayjs from 'dayjs';
import { PromiseToPayTimelineEntry } from '@lmc-app/core/services/promise-to-pay.service';
import {
	PAYMENT_METHOD_LABELS,
	PromiseToPayStatus,
} from '@lmc-app/shared/enum/promise-to-pay-status.enum';
import { FormatUtil } from '@lmc-app/shared/utils/format.util';

/** One changed field of an edit entry, ready to render. */
interface ChangeLine {
	label: string;
	from: string;
	to: string;
}

/**
 * A promise's history, oldest first: each status it moved into and each edit, with who
 * did it and when. Edits list every changed field's old and new value.
 *
 * Reads `PromiseToPay.timeline` as-is — the API already resolved each actor to a name and
 * sorted the entries, so nothing is derived here. Status dots use the same palette as the
 * table badges and the breakdown legend.
 *
 * Not a `mat-stepper`: a stepper models a task the user is working through, with a current
 * step and steps still to come. This is a record of what already happened.
 */
@Component({
	standalone: true,
	selector: 'lmc-promise-to-pay-timeline',
	imports: [CommonModule],
	templateUrl: './promise-to-pay-timeline.component.html',
	styleUrls: ['./promise-to-pay-timeline.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PromiseToPayTimelineComponent {
	@Input() entries: PromiseToPayTimelineEntry[] = [];

	readonly PromiseToPayStatus = PromiseToPayStatus;

	/** "10 Sep 2026, 2:22 pm" — long enough to be unambiguous in a support conversation. */
	formatAt(at: string): string {
		return dayjs(at).format('D MMM YYYY, h:mm a');
	}

	/** Who did it, or what did it when no person was involved. */
	actorLabel(entry: PromiseToPayTimelineEntry): string {
		return entry.byName ?? 'System';
	}

	/** The changed fields of an edit entry; empty for a status change. */
	changeLines(entry: PromiseToPayTimelineEntry): ChangeLine[] {
		const changes = entry.changes;
		if (!changes) return [];
		const lines: ChangeLine[] = [];
		const text = (value: string | null | undefined) => value ?? '—';
		const money = (cents: number | null) =>
			cents == null ? '—' : FormatUtil.formatCurrency(cents / 100);
		if (changes.promiseDate) {
			lines.push({
				label: 'Promise Date',
				from: text(changes.promiseDate.from),
				to: text(changes.promiseDate.to),
			});
		}
		if (changes.amountInCents) {
			lines.push({
				label: 'Amount',
				from: money(changes.amountInCents.from),
				to: money(changes.amountInCents.to),
			});
		}
		if (changes.paymentMethod) {
			const method = (value: string | null) =>
				value
					? PAYMENT_METHOD_LABELS[value as keyof typeof PAYMENT_METHOD_LABELS] ?? value
					: '—';
			lines.push({
				label: 'Payment Method',
				from: method(changes.paymentMethod.from),
				to: method(changes.paymentMethod.to),
			});
		}
		if (changes.ownerId) {
			lines.push({
				label: 'Owner',
				from: text(changes.ownerId.fromName),
				to: text(changes.ownerId.toName),
			});
		}
		return lines;
	}

	trackByEntry(index: number, entry: PromiseToPayTimelineEntry): string {
		return `${entry.at}-${entry.status ?? 'edit'}-${index}`;
	}
}
